import type { ReactNode } from "react"
import classnames from "classnames"
import { Tooltip } from "@mui/material"
import { InfoOutlined } from "@mui/icons-material"
import { kebabCase } from "lodash"
import { buildCurrencyScaleUnit } from "helpers/metrics/currency-scale-label"
import CellTooltip from "components/securities-table/cell-tooltip"
import Row from "./row"

interface PanelColumn {
  id: string
  color: string
  label: string
  type: string | undefined
  noData: boolean | undefined
  isUnmodeled: boolean | undefined
}

interface PanelItem<TData> {
  label: string
  fn: (item: TData, type?: string) => ReactNode
  tooltipContents?: string
  unit?: {
    currencyScale: string
    currency?: string | null
  }
}

interface CompareTablePanelProps<TData> {
  columns: PanelColumn[]
  items?: PanelItem<TData>[]
  data: TData[]
  panelTitle?: string
  titleTooltipContent?: string
  fixedItemNumber?: boolean
}

export default function CompareTablePanel<TData>({
  items,
  data,
  columns,
  panelTitle,
  titleTooltipContent,
  fixedItemNumber,
}: CompareTablePanelProps<TData>) {
  return <div className="compare-panel fixed">
    {panelTitle && <div className={classnames("panel-row", { "fixed-item-number": fixedItemNumber })}>
      <div className="panel-title-wrapper">
        <div className="panel-title">{panelTitle}</div>
        {titleTooltipContent && <Tooltip
          className="panel-title-tooltip"
          title={titleTooltipContent}
          classes={{ popper: "panel-title-tooltip-popper", tooltip: "panel-title-tooltip-content" }}
        >
          <div>
            <InfoOutlined />
          </div>
        </Tooltip>}
      </div>
      {columns.map((column, index) => <Row key={index} column={column} data={data} index={index} selector={undefined} />)}
    </div>}
    {items?.map(({ label, fn, tooltipContents, unit }, index) => {
      const unitLabel = unit && buildCurrencyScaleUnit(unit.currencyScale, unit.currency)
      return <div key={index} className={classnames("panel-row", { "fixed-item-number": fixedItemNumber, "has-unit": unit })}>
        <div data-test-class={`${kebabCase(label)}-label`} className="panel-label" title={unitLabel && `${label} ${unitLabel}`}>
          {label}
          {tooltipContents && <CellTooltip contents={tooltipContents} color={undefined} />}
        </div>
        {unitLabel && <span className="panel-unit">{unitLabel}</span>}
        {columns.map((column, columnIndex) => <Row key={columnIndex} column={column} data={data} index={columnIndex} selector={fn} />)}
      </div>
    })}
  </div>
}
